<template>
	<div :class="[
         {
             'errorField': classError,
         },
         'paste-drop-wrapper'
        ]">
		<div
				class="paste-drop"
				tabindex="0"
				@paste="onPaste"
				@dragover.prevent
				@drop.prevent="onDrop"
		>

			<template v-if="!file && !hasServerPreview"> <!-- CHANGED -->
				<div class="empty">
					<div class="label" :class="{'required': required}">Add screenshot</div>
					<div class="empty-text-wrapper">
						<div class="empty-text">
							<v-icon>mdi-clipboard-outline</v-icon>
							Click here and paste a screenshot with<br>
							<kbd>Ctrl/⌘ + V</kbd> or drag an image file here.
						</div>
					</div>

					<div class="mt-3 d-flex align-center justify-space-between" style="gap:8px">
						<v-btn small @click="triggerPick" :disabled="loading">Choose file</v-btn>
						<v-btn small outlined :disabled="!canUseAsyncClipboard || clipboardBusy" @click="pasteFromClipboard">
							Paste from clipboard
						</v-btn>
					</div>
				</div>
			</template>

			<template v-else>
				<div class="preview">
					<img :src="previewSrc" alt="screenshot" /> <!-- CHANGED -->
					<div class="mt-2 d-flex align-center" style="gap:8px">
			      <span class="caption">
			        {{ displayName }}
			        <template v-if="file && file.size"> ({{ prettySize(file.size) }})</template> <!-- CHANGED -->
			      </span>
						<v-btn small text @click="remove">Clear</v-btn>
					</div>
				</div>
			</template>

			<input
					ref="fileInput"
					type="file"
					accept="image/*"
					hidden
					@change="onPick"
			/>
		</div>

		<div v-if="classError" class="mt-2">
			<div
					v-for="(error, index) in errors"
					:key="index"
					class="error-message label"
			>
				{{ error }}
			</div>
		</div>
	</div>
</template>

<script>

/*
 * PasteImageField — Vue2 + Vuetify2 + Vuelidate 0.7.5
 * Вставка из буфера, drag&drop, file picker, (опц.) ресайз/сжатие.
 * Дополнительно эмитит meta: { name, base64, dataUrl } и update:filename / update:base64.
 */
import { validationMixin } from 'vuelidate';
import { getAppUrl } from "@/services";

export default {
	name: 'PasteImageField',
	mixins: [ validationMixin ],

	props: {
		screenshotEdit: { type: Object, default: null }, // NEW
		classError: Boolean,
		 // v-model: File | dataURL string | { name, value: dataURL }
		value: { type: [ File, Object, String ], default: null },

		/* формат emit: 'file' (по умолч.) | 'dataUrl' | 'object' */
		emitAs: {
			type: String,
			default: 'file',
			validator: v => [ 'file', 'dataUrl', 'object' ].includes(v)
		},

		/* для .sync (необязательно) */
		filename: { type: String, default: null },
		base64: { type: String, default: null },

		required: { type: Boolean, default: false },
		maxSize: { type: Number, default: 5 * 1024 * 1024 },
		accept: { type: Array, default: () => [ 'image/png', 'image/jpeg', 'image/webp' ] },
		baseName: { type: String, default: 'screenshot' },

		/* включить ресайз/сжатие перед emit */
		compress: { type: Boolean, default: false },

		/*
		 * Опции компрессии/ресайза:
		 * maxW, maxH, quality(0..1), mime, downscaleOnly, alwaysCompress
		 */
		compressOptions: {
			type: Object,
			default: () => ({
				maxW: 1920,
				maxH: 1080,
				quality: 0.85,
				mime: 'image/jpeg',
				downscaleOnly: true,
				alwaysCompress: false
			})
		}
	},

	data() {
		return {
			file: null,
			previewUrl: null,
			serverPreview: null, // NEW: { name, url }
			loading: false,
			clipboardBusy: false
		};
	},

	validations() {
		const fileRequired = (f) => !this.required || !!f || this.hasServerPreview; // CHANGED
		const mimeType = (f) => !f || this.hasServerPreview || this.accept.includes(f.type); // CHANGED
		const maxSize = (f) => !f || this.hasServerPreview || f.size <= this.maxSize; // CHANGED
		return { file: { required: fileRequired, mimeType, maxSize } };
	},

	computed: {
		canUseAsyncClipboard() {
			return !!(navigator.clipboard && navigator.clipboard.read);
		},
		errors() {
			if (!this.$v || !this.$v.file) return [];
			const e = [];
			if (!this.$v.file.required) e.push('File is required. ⚠️');
			if (!this.$v.file.mimeType) e.push('Allowed formats: PNG/JPEG/WebP.');
			if (!this.$v.file.maxSize) e.push(`Maximum file size: ${ this.prettySize(this.maxSize) }.`);
			return e;
		},
		compressOpts() {
			const d = {
				maxW: 1920,
				maxH: 1080,
				quality: 0.85,
				mime: 'image/jpeg',
				downscaleOnly: true,
				alwaysCompress: false
			};
			return Object.assign({}, d, this.compressOptions || {});
		},

		hasServerPreview() {
			return !!(this.serverPreview && this.serverPreview.url);
		},
		previewSrc() {
			// Если хочешь подстраховаться на абсолютный URL — раскомментируй resolve:
			const url = this.hasServerPreview ? this.serverPreview.url : this.previewUrl;
			return this.resolveUrl(url);
			// return this.hasServerPreview ? this.serverPreview.url : this.previewUrl;
		},
		displayName() {
			if (this.hasServerPreview) {
				return (this.serverPreview && this.serverPreview.name) ? this.serverPreview.name : 'image';
			}
			if (this.file && this.file.name) {
				return this.file.name;
			}
			return '';
		},
	},

	watch: {
		async value(n) {
			if (n === this.file) return;
			await this.applyExternalValue(n);
		},
		screenshotEdit: { // NEW
			immediate: true,
			handler(n) {
				if (n && n.url) {
					this.serverPreview = { name: n.name || 'image', url: n.url };
					// якщо старий blob-прев’ю висів — прибираємо
					if (!this.file) this.revokePreview();
					// торкнемо валідацію, щоб required не горів
					this.$v && this.$v.file && this.$v.file.$touch();
				} else {
					this.serverPreview = null;
				}
			}
		}
	},

	mounted() {
		this.applyExternalValue(this.value);
	},

	beforeDestroy() {
		this.revokePreview();
	},

	methods: {
		resolveUrl(u) {
			if (!u) return u;
			if (/^https?:\/\//i.test(u) || u.startsWith('blob:') || u.startsWith('data:')) return u;
			// подставь базовый домен API, если нужен:
			// const base = process.env.MIX_BACKEND_URL || window.location.origin;
			const base = getAppUrl;
			return u.startsWith('/') ? base + u : base + '/' + u;
		},
		/* Форматирует байты в b/kb/mb/gb */
		prettySize(bytes) {
			if (!bytes && bytes !== 0) return '';
			const units = [ 'b', 'kb', 'mb', 'gb' ];
			let i = 0; let v = bytes;
			while (v >= 1024 && i < units.length - 1) {
				v /= 1024; i++; 
			}
			return `${ v.toFixed(i === 0 ? 0 : 1) } ${ units[i] }`;
		},

		/* File -> dataURL */
		async fileToDataUrl(file) {
			if (!file) return null;
			return await new Promise((resolve, reject) => {
				const r = new FileReader();
				r.onload = () => resolve(r.result);
				r.onerror = reject;
				r.readAsDataURL(file);
			});
		},

		/* Приводит входное value к File (File | dataURL | {name,value}) */
		async normalizeToFile(val) {
			if (!val) return null;
			if (val instanceof File) return val;
			if (typeof val === 'string' && val.startsWith('data:image/')) {
				return this.dataUrlToFile(val, this.buildFilename('image/png'));
			}
			if (val && typeof val === 'object' && typeof val.value === 'string' && val.value.startsWith('data:image/')) {
				return this.dataUrlToFile(val.value, val.name || this.buildFilename('image/png'));
			}
			return null;
		},

		/* Применяет внешнее value во внутреннее состояние (без emit) */
		async applyExternalValue(val) {
			const f = await this.normalizeToFile(val);
			this.file = f || null;
			this.$v && this.$v.file && this.$v.file.$touch();
			this.revokePreview();
			if (this.file) this.makePreview(this.file);
		},

		/*
		 * Принять файл, (опц.) сжать/ресайзнуть, превью и emit (+ meta/filename/base64).
		 */
		async setFile(f) {
			let out = f || null;

			if (out && this.compress) {
				try {
					out = await this.resizeImage(out, this.compressOpts); 
				} catch (e) {
					void e; 
				}
			}

			if (out) this.serverPreview = null; // NEW: новий файл = відключаємо серверний прев’ю

			this.file = out;
			this.$v && this.$v.file && this.$v.file.$touch();

			this.revokePreview();
			if (this.file) this.makePreview(this.file);

			let dataUrlOnce = null;
			if (this.file) dataUrlOnce = await this.fileToDataUrl(this.file);

			if (this.emitAs === 'file') {
				this.$emit('input', this.file);
			} else if (this.emitAs === 'dataUrl') {
				this.$emit('input', dataUrlOnce);
			} else if (this.emitAs === 'object') {
				this.$emit('input', this.file ? {
					name: this.file.name, type: this.file.type, size: this.file.size, value: dataUrlOnce
				} : null);
			}

			const name = this.file ? this.file.name : null;
			const base64 = dataUrlOnce ? String(dataUrlOnce).split(',')[1] : null;
			this.$emit('meta', { name, base64, dataUrl: dataUrlOnce });
			this.$emit('update:filename', name);
			this.$emit('update:base64', base64);
		},

		/* Создаёт blob: URL для превью */
		makePreview(f) {
			try {
				this.previewUrl = URL.createObjectURL(f);
			} catch (e) {
				this.previewUrl = null; void e;
			}
		},

		/* Освобождает blob: URL */
		revokePreview() {
			if (this.previewUrl) {
				URL.revokeObjectURL(this.previewUrl);
				this.previewUrl = null;
			}
		},

		/* onChange файлового инпута */
		onPick(e) {
			const f = e.target.files && e.target.files[0];
			if (f) this.setFile(f);
			e.target.value = '';
		},

		/* Открыть файловый диалог */
		triggerPick() {
			this.$refs.fileInput && this.$refs.fileInput.click();
		},

		/* Вставка из буфера (paste) */
		onPaste(e) {
			if (!e.clipboardData) return;

			const items = Array.from(e.clipboardData.items || []);
			const imgItem = items.find(it => it.kind === 'file' && it.type.startsWith('image/'));
			if (imgItem) {
				const blob = imgItem.getAsFile();
				if (blob) {
					const file = this.blobToFile(blob, this.buildFilename(blob.type));
					this.setFile(file);
					return;
				}
			}

			const html = e.clipboardData.getData('text/html');
			if (html && html.includes('<img')) {
				const src = this.extractFirstImgDataSrc(html);
				if (src && src.startsWith('data:image/')) {
					const file = this.dataUrlToFile(src, this.buildFilename('image/png'));
					this.setFile(file);
				}
			}
		},

		/* Кнопка "Paste from clipboard" (Async Clipboard API) */
		async pasteFromClipboard() {
			if (!this.canUseAsyncClipboard) return;
			this.clipboardBusy = true;
			try {
				const items = await navigator.clipboard.read();
				for (const item of items) {
					const type = item.types.find(t => t.startsWith('image/'));
					if (type) {
						const blob = await item.getType(type);
						const file = this.blobToFile(blob, this.buildFilename(type));
						this.setFile(file);
						break;
					}
				}
			} catch (e) {
				void e; 
			} finally {
				this.clipboardBusy = false;
			}
		},

		/* DnD drop */
		onDrop(e) {
			const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
			if (f && f.type && f.type.startsWith('image/')) this.setFile(f);
		},

		/* Сброс значения */
		remove() { // CHANGED
			const hadServer = this.hasServerPreview;
			this.setFile(null);
			this.serverPreview = null;
			this.revokePreview();
			this.$v && this.$v.file && this.$v.file.$touch();
			if (hadServer) this.$emit('remove-existing'); // NEW: дай знати батьківському компоненту, що старе фото видалили
			this.$emit('input', null);
		},

		/* Имя файла с таймстампом */
		buildFilename(mimeOrType) {
			const now = new Date();
			const pad = (n) => String(n).padStart(2, '0');
			const yyyy = now.getFullYear();
			const mm = pad(now.getMonth() + 1);
			const dd = pad(now.getDate());
			const hh = pad(now.getHours());
			const mi = pad(now.getMinutes());
			const ss = pad(now.getSeconds());
			const ext = (mimeOrType || '').split('/')[1] || 'png';
			return `${ this.baseName }-${ yyyy }${ mm }${ dd }-${ hh }${ mi }${ ss }.${ ext }`;
		},

		/* Blob -> File */
		blobToFile(blob, filename) {
			try {
				return new File([ blob ], filename, { type: blob.type, lastModified: Date.now() });
			} catch (e) {
				void e;
				const f = blob; f.name = filename; f.lastModified = Date.now();
				return f;
			}
		},

		/* Берёт первый src из <img ...> HTML */
		extractFirstImgDataSrc(html) {
			const m = html.match(/<img[^>]+src=["']([^"']+)["']/i);
			return m ? m[1] : null;
		},

		/* dataURL -> File */
		dataUrlToFile(dataUrl, filename) {
			const parts = dataUrl.split(',');
			const head = parts[0];
			const base64 = parts[1];
			const mime = (head.match(/data:(.*);base64/) || [])[1] || 'image/png';
			const bin = atob(base64);
			const len = bin.length;
			const bytes = new Uint8Array(len);
			for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
			return new File([ bytes ], filename, { type: mime });
		},

		/*
		 * Ресайз/сжатие через <canvas>.
		 * Учитывает downscaleOnly: не увеличиваем изображение, если true.
		 */
		async resizeImage(file, opts) {
			const {
				maxW, maxH, quality, mime, downscaleOnly, alwaysCompress
			} = opts || this.compressOpts || {};
			if (!(file && file.type && file.type.startsWith('image/'))) return file;

			// Пытаемся быстро получить размеры
			let bmp = null;
			if (window.createImageBitmap) {
				try {
					bmp = await createImageBitmap(file); 
				} catch (e) {
					void e; 
				}
			}

			let w, h;
			if (bmp) {
				w = bmp.width; h = bmp.height;
			} else {
				const img = await new Promise((resolve, reject) => {
					const i = new Image();
					i.onload = () => resolve(i);
					i.onerror = reject;
					i.src = URL.createObjectURL(file);
				});
				w = img.naturalWidth || img.width;
				h = img.naturalHeight || img.height;
				if (img && img.src && img.src.startsWith('blob:')) URL.revokeObjectURL(img.src);
			}
			if (!w || !h) return file;

			// Расчёт масштаба
			const sW = maxW > 0 ? (maxW / w) : 1;
			const sH = maxH > 0 ? (maxH / h) : 1;
			let scale = Math.min(sW, sH);

			// Не апскейлим, если запрещено
			if (downscaleOnly && scale > 1) scale = 1;

			const targetW = Math.round(w * scale);
			const targetH = Math.round(h * scale);

			// Ничего не делаем, если 1:1 и не просили "всегда сжимать"
			const shouldProcess = alwaysCompress || (targetW !== w || targetH !== h);
			if (!shouldProcess) {
				if (bmp && typeof bmp.close === 'function') {
					try {
						bmp.close(); 
					} catch (e) {
						void e; 
					} 
				}
				return file;
			}

			// Рисуем на canvas
			const canvas = document.createElement('canvas');
			canvas.width = targetW;
			canvas.height = targetH;
			const ctx = canvas.getContext('2d');

			if (bmp) {
				ctx.drawImage(bmp, 0, 0, targetW, targetH);
				if (typeof bmp.close === 'function') {
					try {
						bmp.close(); 
					} catch (e) {
						void e; 
					} 
				}
			} else {
				const img2 = await new Promise((resolve, reject) => {
					const r = new FileReader();
					r.onload = () => {
						const i = new Image();
						i.onload = () => resolve(i);
						i.onerror = reject;
						i.src = r.result;
					};
					r.onerror = reject;
					r.readAsDataURL(file);
				});
				ctx.drawImage(img2, 0, 0, targetW, targetH);
			}

			const outMime = mime || 'image/jpeg';
			const blob = await new Promise(resolve => {
				canvas.toBlob(b => resolve(b), outMime, quality);
			});

			let outBlob = blob;
			if (!outBlob) {
				const dataUrl = canvas.toDataURL(outMime, quality);
				outBlob = this.dataUrlToFile(dataUrl, file.name).slice(0);
			}

			const newExt = this._extFromMime(outMime);
			const outName = this._replaceExt(file.name || 'image', newExt);
			return this.blobToFile(outBlob, outName);
		},

		_extFromMime(m) {
			if (!m) return 'jpg';
			if (m.includes('webp')) return 'webp';
			if (m.includes('png')) return 'png';
			if (m.includes('jpeg') || m.includes('jpg')) return 'jpg';
			return 'jpg';
		},

		_replaceExt(name, extWithoutDot) {
			const idx = name.lastIndexOf('.');
			const base = idx > 0 ? name.slice(0, idx) : name;
			return `${ base }.${ extWithoutDot }`;
		}
	}
};
</script>

<style scoped lang="scss">
.paste-drop-wrapper {
	background: var(--field-bg);
	-webkit-box-shadow: var(--field-shadow);
	box-shadow: var(--field-shadow);
	padding: 10px;
	border-radius: 4px;

	.paste-drop {
		width: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		text-align: center;
		outline: none;
	}
	.paste-drop:focus { border-color: rgba(0,0,0,.4); }
	.empty-text-wrapper {
		margin-top: 10px;
		.v-icon { display: inline-block; }
		.empty-text { display: inline-block; }
	}
	.empty {
		width: 100%;
		kbd {
			background: rgba(0,0,0,.06);
			padding: 2px 6px;
			border-radius: 4px;
			font-family: inherit;
			color: var(--card-color);
		}

		.label {
			font-weight: 500;
			font-size: 12px;
			line-height: 16px;
			letter-spacing: 0.25px;
			text-align: left;
			&.required:after {
				content: "*";
				color: red;
			}
		}
	}
	.preview img {
		max-width: 100%;
		max-height: 260px;
		margin: 0 auto;
		display: block;
		border-radius: 8px;
	}
	&.errorField {
		.paste-drop {
			.empty {
				.label {
					color: var(--base-red);
				}
			}
		}
		.error-message.label {
			color: var(--base-red);
			line-height: 12px;
			font-size: 12px;
			word-break: break-word;
			overflow-wrap: break-word;
			word-wrap: break-word;
			-ms-hyphens: auto;
			hyphens: auto;
		}
	}
}

</style>
